import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  ViewEncapsulation,
} from '@angular/core';

import { ButtonComponent } from '@akostm/orbit-ui';
import { AppHeaderComponent, AvatarComponent } from '../../atoms';
import { faBars, faCog, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ActiveConversationStore,
  ConversationStore,
  ConversationTypeStore,
  ErrorStore,
  IdentityStore,
  TenantStore,
  UserStore,
} from '../../../stores';
import { ConversationIncludeOption, ParticipantKind, UserRole } from '@clientwire/api';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Dialog } from '@angular/cdk/dialog';
import { CreateConversationFormComponent } from '../create-conversation-form/create-conversation-form.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { ProfileMenuComponent } from '../../molecules';
import { Router } from '@angular/router';
import { UserPreferencesStore } from '../../../stores/user-preferences.store';
import { ToastrService } from 'ngx-toastr';
import { ConversationHelperService } from '../../../services';

@Component({
  selector: 'shared-dashboard-header',
  standalone: true,
  imports: [ButtonComponent, AvatarComponent, TranslateModule, AppHeaderComponent],
  templateUrl: './dashboard-header.component.html',
  styleUrl: './dashboard-header.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardHeaderComponent {
  readonly createIcon = faPlusCircle;
  readonly settingsIcon = faCog;
  readonly menuIcon = faBars;
  #profileOverlayRef: OverlayRef | null = null;
  readonly #resetPasswordErrorCodes = ['error.http.not_found'];

  //#region Inputs
  //#endregion

  //#region Outputs
  readonly toggleConversationList = output<void>();
  //#endregion

  //#region Injectables
  readonly userStore = inject(UserStore);
  readonly #dialog = inject(Dialog);
  readonly conversationStore = inject(ConversationStore);
  readonly conversationTypeStore = inject(ConversationTypeStore);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #userPreferencesStore = inject(UserPreferencesStore);
  readonly #tenantStore = inject(TenantStore);
  readonly #overlay = inject(Overlay);
  readonly #identityStore = inject(IdentityStore);
  readonly #router = inject(Router);
  readonly #errorStore = inject(ErrorStore);
  readonly #toastr = inject(ToastrService);
  readonly #translateService = inject(TranslateService);
  //#endregion

  //#region Computeds
  readonly isOwner = computed(() => this.userStore.role() === UserRole.Owner);
  readonly nameAbbreviation = computed(() =>
    (
      (this.userStore.firstname()?.[0] ?? '') +
      '' +
      (this.userStore.lastname()?.[0] ?? '')
    ).toUpperCase()
  );
  readonly #userId = computed(() => this.userStore.id());
  readonly resetPasswordErrors = computed(() =>
    this.#errorStore.getErrorsByCodesAndType(this.#resetPasswordErrorCodes, 'inline')
  );
  readonly #error = computed(() => this.#errorStore.getErrorsByLocations(['createConversation']));

  openCreateConversationDialog(): void {
    const error = this.#error;
    const dialogRef = this.#dialog.open(CreateConversationFormComponent, {
      width: '61.25rem',
      panelClass: 'my-dialog',
      data: {
        generatedID: this.#generateUID(),
        conversationTypes: this.conversationTypeStore.types(),
        preselectedConversationType: this.#userPreferencesStore.preferredConversationTypeId(),
        error,
      },
    });

    dialogRef.componentInstance?.favoriteConversationTypeSelected.subscribe(async (type) => {
      await this.#userPreferencesStore.updatePreferredConversationTypeId(this.#userId(), type?.id);
    });
    dialogRef.componentInstance?.dialogButtonClicked.subscribe(async (form) => {
      if (form.eventType === 'SAVE' && form.data && this.#tenantStore.id()) {
        const participants = form.data.participants;
        const conversation = await this.conversationStore.createNewConversation(
          {
            id: form.data.id,
            conversationTypeId: form.data.type,
            description: form.data.description,
            conversationData: form.data.data ? JSON.parse(form.data.data) : null,
            participants: participants.map((participant) => {
              return {
                id: participant.id,
                kind: ParticipantKind.Client,
                displayName: participant.name,
                phoneNumber: participant.phone,
                email: participant.email,
                preferredLanguage: participant.preferredLanguage ?? undefined,
              };
            }),
          },
          [
            ConversationIncludeOption.Participants,
            ConversationIncludeOption.ConversationType,
            ConversationIncludeOption.ListItemTemplateData,
            ConversationIncludeOption.ClientParticipantFrontendUrl,
            ConversationIncludeOption.ParticipantNotificationStatus,
          ]
        );
        if (error()?.length === 0) {
          dialogRef.close();
          if (conversation) {
            const isActive = this.#activeConversationStore.entityMap()[conversation.id];
            if (!isActive) {
              this.#conversationHelperService.addToActiveConversations([conversation]);
            }
          }
        }

        dialogRef.componentInstance?.formSubmitting.set(false);
      }
      if (form.eventType === 'CANCEL') {
        this.conversationStore.clearError();
        dialogRef.close();
      }
    });
  }

  openProfileOverlay(event: HTMLElement) {
    if (event) {
      if (this.#profileOverlayRef) {
        this.#closeProfileMenu();
      } else {
        const positionStrategy = this.#overlay
          .position()
          .flexibleConnectedTo(event)
          .withPositions([
            {
              originX: 'end',
              originY: 'bottom',
              overlayX: 'end',
              overlayY: 'top',
              offsetY: 5,
            } as ConnectedPosition,
          ])
          .withPush(false);
        const scrollStrategy = this.#overlay.scrollStrategies.close();
        this.#profileOverlayRef = this.#overlay.create({
          positionStrategy: positionStrategy,
          scrollStrategy: scrollStrategy,
        });
        const more = new ComponentPortal(ProfileMenuComponent);
        const ref = this.#profileOverlayRef.attach(more);
        ref.setInput('name', this.userStore.firstname() + ' ' + this.userStore.lastname());
        ref.setInput('email', this.userStore.email());
        ref.setInput(
          'resetPasswordButtonLabel',
          this.#translateService.instant('SHARED.PAGES.DASHBOARD.RESET_PASSWORD_BUTTON')
        );
        ref.setInput(
          'logoutButtonLabel',
          this.#translateService.instant('SHARED.PAGES.DASHBOARD.LOGOUT_BUTTON')
        );
        ref.instance.closeOverlay.subscribe(() => {
          this.#closeProfileMenu();
        });
        ref.instance.logout.subscribe(() => {
          this.#closeProfileMenu();
          this.#logout();
        });
        ref.instance.resetPassword.subscribe(() => {
          this.#closeProfileMenu();
          this.#resetPassword();
        });
        this.#profileOverlayRef._outsidePointerEvents.subscribe(() => {
          this.#closeProfileMenu();
        });
      }
    }
  }

  navigateToSettings() {
    this.#router.navigate(['/settings']);
  }

  #closeProfileMenu() {
    // Settime out is needed becaouse of the outsidePointerEvents triggering first and closing the overlay, then reopening
    setTimeout(() => {
      this.#profileOverlayRef?.detach();
      this.#profileOverlayRef = null;
    }, 0);
  }

  #logout() {
    this.#identityStore.logout();
  }

  async #resetPassword() {
    this.#errorStore.clearErrorsByCodes(this.#resetPasswordErrorCodes);
    await this.#identityStore.resetPassword({ email: this.userStore.email() });
    if (this.resetPasswordErrors().length === 0) {
      this.#toastr.success(
        this.#translateService.instant('SHARED.PAGES.RESET_PASSWORD.RESET_EMAIL_HAS_BEEN_SENT')
      );
    }
  }

  #generateUID() {
    let firstPart: string | number = (Math.random() * 46656) | 0;
    let secondPart: string | number = (Math.random() * 46656) | 0;
    firstPart = ('000' + firstPart.toString(36)).slice(-3);
    secondPart = ('000' + secondPart.toString(36)).slice(-3);
    return `${firstPart.toUpperCase()}${secondPart.toUpperCase()}`;
  }

  onToggleConversationListKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleConversationList.emit();
    }
  }

  onOpenCreateConversationDialogKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openCreateConversationDialog();
    }
  }

  onNavigateToSettingsKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.navigateToSettings();
    }
  }
}
